import React, { useState, useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Loader2, ImageOff, Info } from "lucide-react";
import { trpc } from "@/lib/trpc";
import "./ImageGroup.css";

export interface ImageItem {
    url: string;
    alt?: string;
    width?: number;
    height?: number;
    source?: string;
    license?: string;
    attribution?: string;
}

export interface ImageQuery {
    query: string;
    limit?: number;
}

export interface ImageGroupProps {
    /** Pre-resolved images (if already loaded) */
    images?: ImageItem[];
    /** Image queries to resolve dynamically */
    queries?: ImageQuery[];
    layout?: "carousel" | "single";
    caption?: string;
    className?: string;
}

/**
 * ImageGroup component for rendering images in assistant message bubbles.
 * Supports both pre-resolved images and dynamic loading via queries.
 */
const ImageGroup: React.FC<ImageGroupProps> = ({
    images: preloadedImages,
    queries,
    layout = "carousel",
    caption,
    className,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loadedImages, setLoadedImages] = useState<ImageItem[]>(preloadedImages || []);
    const [showInfo, setShowInfo] = useState<number | null>(null);

    // Use tRPC to search for images from all providers
    const { data, isLoading, error } = trpc.images.searchAll.useQuery(
        {
            query: queries?.[0]?.query || "",
            limit: queries?.[0]?.limit || 3,
        },
        {
            enabled: !!queries && queries.length > 0 && !!queries[0]?.query && (!preloadedImages || preloadedImages.length === 0),
            staleTime: 5 * 60 * 1000, // Cache for 5 minutes
            refetchOnWindowFocus: false,
        }
    );

    // Update loaded images when data arrives
    useEffect(() => {
        console.log("[ImageGroup] Data received:", data);
        if (data?.success && data.images.length > 0) {
            const mappedImages = data.images.map(img => ({
                url: img.url,
                alt: (img as any).alt || (img as any).title || "Image",
                width: img.width,
                height: img.height,
                source: (img as any).source,
                license: (img as any).license,
                attribution: (img as any).attribution,
            }));
            console.log("[ImageGroup] Mapped images:", mappedImages);
            setLoadedImages(mappedImages);
        }
    }, [data]);

    // Use preloaded images if provided
    useEffect(() => {
        if (preloadedImages && preloadedImages.length > 0) {
            setLoadedImages(preloadedImages);
        }
    }, [preloadedImages]);

    const goToNext = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % loadedImages.length);
    }, [loadedImages.length]);

    const goToPrev = useCallback(() => {
        setCurrentIndex((prev) => (prev - 1 + loadedImages.length) % loadedImages.length);
    }, [loadedImages.length]);

    const goToIndex = useCallback((index: number) => {
        setCurrentIndex(index);
    }, []);

    // Loading state
    if (isLoading) {
        return (
            <div className={cn("my-3", className)}>
                <div className="flex items-center justify-center h-[200px] rounded-lg border border-border/50 bg-muted/20">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span className="text-xs">Loading images...</span>
                    </div>
                </div>
                {caption && (
                    <p className="mt-2 text-center text-xs text-muted-foreground">
                        {caption}
                    </p>
                )}
            </div>
        );
    }

    // Error or no images state
    if (error || loadedImages.length === 0) {
        return (
            <div className={cn("my-3", className)}>
                <div className="flex items-center justify-center h-[120px] rounded-lg border border-border/50 bg-muted/10">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <ImageOff className="w-5 h-5" />
                        <span className="text-xs">No images available</span>
                    </div>
                </div>
                {caption && (
                    <p className="mt-2 text-center text-xs text-muted-foreground">
                        {caption}
                    </p>
                )}
            </div>
        );
    }

    // For single layout, only show the first image
    const displayImages = layout === "single" ? [loadedImages[0]] : loadedImages;
    const isSingleImage = displayImages.length === 1;

    // Single image layout
    if (isSingleImage) {
        const image = displayImages[0];
        return (
            <div className={cn("mt-3 mb-1 image-group-container", className)}>
                <div className="w-full rounded-lg overflow-hidden relative group/image">
                    <img
                        src={image.url}
                        alt={image.alt || "Image"}
                        className="w-full h-[280px] object-cover object-center cursor-pointer block"
                        style={{ minWidth: '100%' }}
                        loading="lazy"
                        onClick={() => window.open(image.url, '_blank')}
                    />
                    {/* Info icon */}
                    {image.source && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowInfo(showInfo === 0 ? null : 0);
                            }}
                            className={cn(
                                "absolute top-10 right-10 z-10",
                                "w-7 h-7 rounded-full bg-background/80 backdrop-blur-sm",
                                "flex items-center justify-center",
                                "border border-border shadow-md",
                                "opacity-0 group-hover/image:opacity-100 transition-opacity",
                                "hover:bg-background cursor-pointer"
                            )}
                            aria-label="Image information"
                        >
                            <Info className="w-4 h-4" />
                        </button>
                    )}
                    {/* Info overlay */}
                    {showInfo === 0 && image.source && (
                        <div 
                            className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-20"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowInfo(null);
                            }}
                        >
                            <div className="bg-background/95 rounded-lg p-4 max-w-sm shadow-xl border border-border">
                                <div className="space-y-2 text-sm">
                                    <div>
                                        <span className="font-semibold text-foreground">Source:</span>
                                        <span className="ml-2 text-muted-foreground">{image.source}</span>
                                    </div>
                                    {image.license && (
                                        <div>
                                            <span className="font-semibold text-foreground">License:</span>
                                            <span className="ml-2 text-muted-foreground">{image.license}</span>
                                        </div>
                                    )}
                                    {image.attribution && (
                                        <div>
                                            <span className="font-semibold text-foreground">Attribution:</span>
                                            <p className="mt-1 text-xs text-muted-foreground">{image.attribution}</p>
                                        </div>
                                    )}
                                </div>
                                <p className="mt-3 text-xs text-center text-muted-foreground">
                                    Click anywhere to close
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                {caption && (
                    <p className="mt-1 text-center text-xs text-muted-foreground">
                        {caption}
                    </p>
                )}
            </div>
        );
    }

    // Carousel layout - one image at a time
    const currentImage = displayImages[currentIndex];

    return (
        <div className={cn("my-3 relative group/carousel w-full image-group-container", className)}>
            {/* Image container */}
            <div className="relative w-full overflow-hidden rounded-lg">
                {/* Left navigation arrow */}
                <button
                    onClick={goToPrev}
                    className={cn(
                        "absolute left-4 top-1/2 -translate-y-1/2 z-10",
                        "w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm",
                        "flex items-center justify-center",
                        "border border-border shadow-md",
                        "opacity-0 group-hover/carousel:opacity-100 transition-opacity",
                        "hover:bg-background cursor-pointer"
                    )}
                    aria-label="Previous image"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>

                {/* Right navigation arrow */}
                <button
                    onClick={goToNext}
                    className={cn(
                        "absolute right-4 top-1/2 -translate-y-1/2 z-10",
                        "w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm",
                        "flex items-center justify-center",
                        "border border-border shadow-md",
                        "opacity-0 group-hover/carousel:opacity-100 transition-opacity",
                        "hover:bg-background cursor-pointer"
                    )}
                    aria-label="Next image"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>

                {/* Current image */}
                <img
                    src={currentImage.url}
                    alt={currentImage.alt || `Image ${currentIndex + 1}`}
                    className="w-full h-[280px] object-cover object-center cursor-pointer block"
                    style={{ minWidth: '100%' }}
                    loading="lazy"
                    onClick={() => window.open(currentImage.url, '_blank')}
                />

                {/* Image counter badge */}
                <div className="absolute top-10 right-10 bg-background/70 backdrop-blur-sm px-2 py-0.5 rounded-full text-xs text-foreground/80">
                    {currentIndex + 1} / {displayImages.length}
                </div>

                {/* Info icon */}
                {currentImage.source && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowInfo(showInfo === currentIndex ? null : currentIndex);
                        }}
                        className={cn(
                            "absolute top-10 left-10 z-10",
                            "w-7 h-7 rounded-full bg-background/80 backdrop-blur-sm",
                            "flex items-center justify-center",
                            "border border-border shadow-md",
                            "opacity-0 group-hover/carousel:opacity-100 transition-opacity",
                            "hover:bg-background cursor-pointer"
                        )}
                        aria-label="Image information"
                    >
                        <Info className="w-4 h-4" />
                    </button>
                )}

                {/* Info overlay */}
                {showInfo === currentIndex && currentImage.source && (
                    <div 
                        className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-20"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowInfo(null);
                        }}
                    >
                        <div className="bg-background/95 rounded-lg p-4 max-w-sm shadow-xl border border-border">
                            <div className="space-y-2 text-sm">
                                <div>
                                    <span className="font-semibold text-foreground">Source:</span>
                                    <span className="ml-2 text-muted-foreground">{currentImage.source}</span>
                                </div>
                                {currentImage.license && (
                                    <div>
                                        <span className="font-semibold text-foreground">License:</span>
                                        <span className="ml-2 text-muted-foreground">{currentImage.license}</span>
                                    </div>
                                )}
                                {currentImage.attribution && (
                                    <div>
                                        <span className="font-semibold text-foreground">Attribution:</span>
                                        <p className="mt-1 text-xs text-muted-foreground">{currentImage.attribution}</p>
                                    </div>
                                )}
                            </div>
                            <p className="mt-3 text-xs text-center text-muted-foreground">
                                Click anywhere to close
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Dot indicators */}
            <div className="flex justify-center gap-1.5 mt-2">
                {displayImages.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => goToIndex(index)}
                        className={cn(
                            "w-2 h-2 rounded-full transition-all duration-200 cursor-pointer",
                            index === currentIndex
                                ? "bg-primary w-4"
                                : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                        )}
                        aria-label={`Go to image ${index + 1}`}
                    />
                ))}
            </div>

            {caption && (
                <p className="mt-2 text-center text-xs text-muted-foreground">
                    {caption}
                </p>
            )}
        </div>
    );
};

export default ImageGroup;
